import React from 'react';
import { Redirect, Route } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import LockIcon from '@material-ui/icons/Lock';
import { useHistory } from 'react-router-dom';

import { useCurrentUser } from 'providers/user/UserContext';
import useFeatureAccess from 'utils/useFeatureAccess';
import { FEATURES, getTierLabel, getMinimumTierForFeature } from 'utils/featureGating';

const YELLOW = '#edd714';
const YELLOW_GLOW = 'rgba(237, 215, 20, 0.22)';

const useStyles = makeStyles(() => ({
    root: {
        minHeight: '100vh',
        backgroundColor: '#000000',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Exo 2, sans-serif',
        padding: '40px',
    },
    lockWrap: {
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        backgroundColor: 'rgba(237, 215, 20, 0.08)',
        border: `1px solid rgba(237, 215, 20, 0.15)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '24px',
    },
    lockIcon: {
        fontSize: '36px',
        color: YELLOW,
    },
    title: {
        fontFamily: 'Orbitron, sans-serif',
        fontSize: '40px',
        letterSpacing: '4px',
        color: '#ffffff',
        marginBottom: '12px',
    },
    subtitle: {
        fontFamily: 'Exo 2',
        fontSize: '14px',
        fontWeight: 500,
        color: '#888888',
        textAlign: 'center',
        maxWidth: '400px',
        lineHeight: 1.7,
        marginBottom: '32px',
    },
    tierText: {
        color: YELLOW,
        fontWeight: 800,
    },
    upgradeBtn: {
        fontFamily: 'Exo 2',
        fontWeight: 800,
        fontSize: '13px',
        letterSpacing: '1.5px',
        borderRadius: '8px',
        backgroundColor: YELLOW,
        color: '#000000',
        padding: '14px 40px',
        textTransform: 'uppercase',
        boxShadow: `0 4px 18px ${YELLOW_GLOW}`,
        '&:hover': {
            backgroundColor: '#f5e419',
            boxShadow: `0 6px 24px ${YELLOW_GLOW}`,
            transform: 'scale(1.02)',
        },
    },
    currentPlan: {
        fontFamily: 'Exo 2',
        fontSize: '11px',
        fontWeight: 700,
        color: '#333333',
        letterSpacing: '1.5px',
        marginTop: '20px',
        textTransform: 'uppercase',
    },
}));

/**
 * TieredRoute — A route wrapper that checks subscription tier access.
 *
 * Works like PrivateRoute but also gates on a feature.
 * If the user is logged in but doesn't have the required tier,
 * they see a full-page upgrade prompt instead of a redirect.
 *
 * Props:
 *   - featureId: string — key from FEATURES map
 *   - children: route content
 *   - ...rest: passed to Route
 */
export default function TieredRoute({ featureId, children, ...rest }) {
    const { loading, data } = useCurrentUser();
    const { canAccess, userType, tierLabel } = useFeatureAccess();
    const classes = useStyles();
    const history = useHistory();

    const feature = FEATURES[featureId];
    const featureName = feature?.name || 'This feature';
    const requiredTierId = getMinimumTierForFeature(featureId, userType);
    const requiredTierLabel = getTierLabel(requiredTierId);

    const render = ({ location }) => {
        if (loading) return null;

        // Not logged in → redirect to login
        if (!data) {
            return (
                <Redirect to={{ pathname: '/login', state: { from: location } }} />
            );
        }

        // Logged in but doesn't have the required feature tier
        if (!canAccess(featureId)) {
            return (
                <div className={classes.root}>
                    <div className={classes.lockWrap}>
                        <LockIcon className={classes.lockIcon} />
                    </div>
                    <div className={classes.title}>UPGRADE REQUIRED</div>
                    <div className={classes.subtitle}>
                        <strong>{featureName}</strong> is available on the{' '}
                        <span className={classes.tierText}>{requiredTierLabel}</span>{' '}
                        plan and above. Upgrade your subscription to access this feature.
                    </div>
                    <Button
                        className={classes.upgradeBtn}
                        onClick={() => history.push('/store')}
                        disableElevation
                    >
                        Upgrade Now →
                    </Button>
                    <div className={classes.currentPlan}>
                        Your plan: {tierLabel}
                    </div>
                </div>
            );
        }

        // Has access → render children
        return children;
    };

    return <Route {...rest} render={render} />;
}
